import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
    WhatsappLogo,
    ChatCircleText,
    Envelope,
    PencilSimple,
    Trash,
    Plus,
    MagicWand,
    Robot,
    Lightning,
    TextAa,
    Copy,
    Eye,
    Warning
} from '@phosphor-icons/react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Character limits per channel
const CHANNEL_LIMITS = {
    sms: { max: 160, segment: 160, warn: 140, label: 'SMS' },          // 1 SMS = 160 chars GSM
    whatsapp: { max: 4096, segment: null, warn: 1000, label: 'WhatsApp' },
    email: { max: null, segment: null, warn: null, label: 'Email' }     // unlimited
};

const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
};

const MessageTemplatesPage = () => {
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showEditor, setShowEditor] = useState(false);
    const [editingTemplate, setEditingTemplate] = useState(null);
    const [formData, setFormData] = useState({ name: '', type: 'sale_confirmation', channel: 'whatsapp', subject: '', body: '' });
    const [aiMode, setAiMode] = useState(null); // null, 'assist', 'full'
    const [aiInstruction, setAiInstruction] = useState('');
    const [generating, setGenerating] = useState(false);
    const [previewTemplate, setPreviewTemplate] = useState(null);

    const sampleData = {
        nombre_cliente: "María García López",
        folio: "CC-260430-0001",
        folios_listado: "• Escapada Riviera Maya — Folio: CC-260430-0001\n• Tour Cancún 3D2N — Folio: CC-260430-0002",
        cantidad_certificados: "2",
        producto: "Escapada 4 Estrellas Riviera Maya",
        total: "$4,999",
        link_certificado: "https://cert-voucher-sales.preview.emergentagent.com/cert/CC-260430-0001",
        link_portal: "https://cert-voucher-sales.preview.emergentagent.com/me/login",
        empresa: "Exploremos"
    };

    const fillPreview = (text) => {
        if (!text) return text;
        let filled = text;
        for (const [key, value] of Object.entries(sampleData)) {
            filled = filled.replaceAll(`{${key}}`, value);
        }
        return filled;
    };

    useEffect(() => { fetchTemplates(); }, []);

    const fetchTemplates = async () => {
        try {
            const response = await axios.get(`${API}/message-templates`, { headers: getAuthHeaders() });
            setTemplates(response.data);
        } catch (error) {
            toast.error('Error al cargar plantillas');
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        if (!formData.name || !formData.body) {
            toast.error('Nombre y contenido son obligatorios');
            return;
        }
        try {
            if (editingTemplate) {
                await axios.put(`${API}/message-templates/${editingTemplate.id}`, {
                    name: formData.name,
                    subject: formData.subject || null,
                    body: formData.body
                }, { headers: getAuthHeaders() });
                toast.success('Plantilla actualizada');
            } else {
                await axios.post(`${API}/message-templates`, formData, { headers: getAuthHeaders() });
                toast.success('Plantilla creada');
            }
            fetchTemplates();
            setShowEditor(false);
            resetForm();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Error al guardar');
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('¿Eliminar esta plantilla?')) return;
        try {
            await axios.delete(`${API}/message-templates/${id}`, { headers: getAuthHeaders() });
            toast.success('Plantilla eliminada');
            fetchTemplates();
        } catch (error) {
            toast.error('Error al eliminar');
        }
    };

    const handleToggleActive = async (template) => {
        const newState = !template.is_active;
        // Optimistic update
        setTemplates(prev => prev.map(t => t.id === template.id ? { ...t, is_active: newState } : t));
        try {
            await axios.put(`${API}/message-templates/${template.id}`, {
                is_active: newState
            }, { headers: getAuthHeaders() });
            toast.success(newState ? 'Plantilla activada' : 'Plantilla desactivada');
        } catch (error) {
            // Revert on failure
            setTemplates(prev => prev.map(t => t.id === template.id ? { ...t, is_active: !newState } : t));
            toast.error('Error al cambiar estado');
        }
    };

    const handleAIGenerate = async () => {
        setGenerating(true);
        try {
            const response = await axios.post(`${API}/message-templates/generate-ai`, {
                type: formData.type,
                channel: formData.channel,
                instruction: aiMode === 'assist' ? formData.body : aiInstruction,
                mode: aiMode
            }, { headers: getAuthHeaders() });
            
            setFormData(prev => ({
                ...prev,
                body: response.data.body,
                subject: response.data.subject || prev.subject
            }));
            toast.success('Texto generado por IA');
            setAiMode(null);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Error al generar con IA');
        } finally {
            setGenerating(false);
        }
    };

    const resetForm = () => {
        setFormData({ name: '', type: 'sale_confirmation', channel: 'whatsapp', subject: '', body: '' });
        setEditingTemplate(null);
        setAiMode(null);
        setAiInstruction('');
    };

    const openEditor = (template = null) => {
        if (template) {
            setEditingTemplate(template);
            setFormData({
                name: template.name,
                type: template.type,
                channel: template.channel,
                subject: template.subject || '',
                body: template.body
            });
        } else {
            resetForm();
        }
        setShowEditor(true);
    };

    const channelIcon = (channel) => {
        switch (channel) {
            case 'whatsapp': return <WhatsappLogo size={16} className="text-[#25D366]" />;
            case 'sms': return <ChatCircleText size={16} className="text-[#1F2D4B]" />;
            case 'email': return <Envelope size={16} className="text-[#FB7185]" />;
            default: return null;
        }
    };

    const typeLabel = (type) => {
        switch (type) {
            case 'welcome': return 'Bienvenida';
            case 'sale_confirmation': return 'Confirmación';
            case 'reminder': return 'Recordatorio';
            default: return type;
        }
    };

    const variables = ['{nombre_cliente}', '{folio}', '{folios_listado}', '{producto}', '{total}', '{cantidad_certificados}', '{link_portal}', '{link_certificado}', '{empresa}'];

    // Validador y preview de plantillas — usa datos de muestra para que el admin
    // vea exactamente cómo se verá el mensaje al cliente final.
    const SAMPLE_VARS = {
        nombre_cliente: 'María García',
        folio: 'ES4-260612-0042',
        folios_listado: '• Escapada Riviera Maya — Folio: ES4-260612-0042\n• Vuelo Cancún — Folio: VL-260612-0007',
        producto: 'Escapada Riviera Maya',
        total: '$8,450',
        cantidad_certificados: '2',
        link_portal: 'https://pos.exploremos.mx/me/login',
        link_certificado: 'https://pos.exploremos.mx/cert/ES4-260612-0042',
        empresa: 'Exploremos',
    };

    const validVarNames = variables.map(v => v.slice(1, -1)); // sin las llaves

    /**
     * Analiza un texto y devuelve { preview, invalidVars }
     *  - preview: texto con variables sustituidas por valores de muestra
     *  - invalidVars: array de variables que el admin escribió pero NO existen
     */
    const analyzeTemplate = (text) => {
        if (!text) return { preview: '', invalidVars: [] };
        let preview = text;
        const found = [...text.matchAll(/\{([a-zA-Z_][a-zA-Z0-9_]*)\}/g)].map(m => m[1]);
        const invalidVars = [...new Set(found.filter(v => !validVarNames.includes(v)))];
        Object.entries(SAMPLE_VARS).forEach(([key, value]) => {
            preview = preview.replaceAll(`{${key}}`, value);
        });
        return { preview, invalidVars };
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1F2D4B]"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="templates-page">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="font-editorial text-2xl sm:text-3xl font-medium text-[#1F2D4B]">
                        Plantillas de Mensajes
                    </h1>
                    <p className="text-[#4B5878] mt-1">
                        Configura los mensajes automáticos para cada canal
                    </p>
                </div>
                <Button
                    onClick={() => openEditor()}
                    data-testid="create-template-btn"
                    className="h-11 bg-[#1F2D4B] hover:bg-[#2E3F60] text-white rounded-xl"
                >
                    <Plus size={18} className="mr-2" />
                    Nueva Plantilla
                </Button>
            </div>

            {/* Templates Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {templates.map((template) => {
                    const limit = CHANNEL_LIMITS[template.channel];
                    const len = template.body?.length || 0;
                    const segments = limit?.segment ? Math.ceil(len / limit.segment) : null;
                    return (
                    <Card 
                        key={template.id} 
                        className={`bg-white border-[#ECE3D2] rounded-xl transition-all ${
                            template.is_active 
                                ? 'hover:border-[#1F2D4B]/30' 
                                : 'opacity-60 grayscale'
                        }`}
                        data-testid={`template-card-${template.id}`}
                    >
                        <CardContent className="p-5">
                            <div className="flex items-start justify-between mb-3 gap-2">
                                <div className="flex items-start gap-2 min-w-0 flex-1">
                                    <div className="flex-shrink-0 mt-0.5">
                                        {channelIcon(template.channel)}
                                    </div>
                                    <div className="min-w-0">
                                        <p className="font-semibold text-[#1F2D4B] text-sm truncate">{template.name}</p>
                                        <div className="flex items-center gap-1.5 mt-1">
                                            <Badge className={`border-0 text-[10px] px-1.5 py-0.5 ${
                                                template.type === 'welcome' ? 'bg-purple-50 text-purple-700' :
                                                template.type === 'sale_confirmation' ? 'bg-[#D1FAE5] text-[#059669]' :
                                                'bg-amber-50 text-amber-700'
                                            }`}>
                                                {typeLabel(template.type)}
                                            </Badge>
                                            <span className="text-[10px] text-[#8B8474]">
                                                {len} car{segments && segments > 1 ? ` · ${segments} SMS` : ''}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {/* Toggle Switch */}
                                <div className="flex flex-col items-center gap-1 flex-shrink-0">
                                    <Switch
                                        checked={template.is_active}
                                        onCheckedChange={() => handleToggleActive(template)}
                                        data-testid={`toggle-template-${template.id}`}
                                        className="data-[state=checked]:bg-[#1F2D4B]"
                                    />
                                    <span className={`text-[9px] font-semibold uppercase tracking-wider ${
                                        template.is_active ? 'text-[#059669]' : 'text-[#8B8474]'
                                    }`}>
                                        {template.is_active ? 'Activa' : 'Inactiva'}
                                    </span>
                                </div>
                            </div>

                            <p className="text-xs text-[#4B5878] line-clamp-3 mb-3 leading-relaxed">{template.body}</p>

                            <div className="flex items-center gap-1 pt-2 border-t border-[#ECE3D2]">
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setPreviewTemplate(template)}
                                    className="text-[#4B5878] hover:text-[#1F2D4B] h-8 text-xs"
                                    data-testid={`preview-template-${template.id}`}
                                >
                                    <Eye size={13} className="mr-1" />
                                    Ver
                                </Button>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => openEditor(template)}
                                    className="text-[#4B5878] hover:text-[#1F2D4B] h-8 text-xs"
                                    data-testid={`edit-template-${template.id}`}
                                >
                                    <PencilSimple size={13} className="mr-1" />
                                    Editar
                                </Button>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleDelete(template.id)}
                                    className="ml-auto text-red-400 hover:text-red-600 h-8 text-xs"
                                    data-testid={`delete-template-${template.id}`}
                                >
                                    <Trash size={13} />
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                    );
                })}
            </div>

            {templates.length === 0 && (
                <div className="text-center py-12">
                    <TextAa size={48} className="mx-auto text-[#E5E7EB] mb-3" />
                    <p className="text-[#4B5878]">No hay plantillas configuradas</p>
                    <Button onClick={() => openEditor()} className="mt-4 bg-[#1F2D4B] hover:bg-[#2E3F60] text-white rounded-xl">
                        Crear primera plantilla
                    </Button>
                </div>
            )}

            {/* Editor Dialog */}
            <Dialog open={showEditor} onOpenChange={setShowEditor}>
                <DialogContent className="sm:max-w-2xl rounded-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="font-editorial text-xl text-[#1F2D4B]">
                            {editingTemplate ? 'Editar Plantilla' : 'Nueva Plantilla'}
                        </DialogTitle>
                    </DialogHeader>
                    
                    <div className="space-y-5 pt-2">
                        {/* Name */}
                        <div>
                            <Label className="text-sm font-medium text-[#1F2D4B]">Nombre</Label>
                            <Input
                                value={formData.name}
                                onChange={(e) => setFormData({...formData, name: e.target.value})}
                                placeholder="Ej: Confirmación de venta WhatsApp"
                                className="mt-1 h-11 rounded-xl border-[#ECE3D2] bg-white text-[#1F2D4B]"
                                data-testid="template-name-input"
                            />
                        </div>

                        {/* Type and Channel */}
                        {!editingTemplate && (
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <Label className="text-sm font-medium text-[#1F2D4B]">Tipo</Label>
                                    <select
                                        value={formData.type}
                                        onChange={(e) => setFormData({...formData, type: e.target.value})}
                                        className="mt-1 w-full h-11 rounded-xl border border-[#ECE3D2] bg-white text-[#1F2D4B] px-3 text-sm"
                                    >
                                        <option value="welcome">Bienvenida</option>
                                        <option value="sale_confirmation">Confirmación de Venta</option>
                                        <option value="reminder">Recordatorio</option>
                                    </select>
                                </div>
                                <div>
                                    <Label className="text-sm font-medium text-[#1F2D4B]">Canal</Label>
                                    <select
                                        value={formData.channel}
                                        onChange={(e) => setFormData({...formData, channel: e.target.value})}
                                        className="mt-1 w-full h-11 rounded-xl border border-[#ECE3D2] bg-white text-[#1F2D4B] px-3 text-sm"
                                    >
                                        <option value="whatsapp">WhatsApp</option>
                                        <option value="sms">SMS</option>
                                        <option value="email">Email</option>
                                    </select>
                                </div>
                            </div>
                        )}

                        {/* Subject (email only) */}
                        {formData.channel === 'email' && (
                            <div>
                                <Label className="text-sm font-medium text-[#1F2D4B]">Asunto del Email</Label>
                                <Input
                                    value={formData.subject}
                                    onChange={(e) => setFormData({...formData, subject: e.target.value})}
                                    placeholder="Ej: Tu certificado de viaje - {folio}"
                                    className="mt-1 h-11 rounded-xl border-[#ECE3D2] bg-white text-[#1F2D4B]"
                                />
                            </div>
                        )}

                        {/* Body */}
                        <div>
                            {(() => {
                                const limit = CHANNEL_LIMITS[formData.channel];
                                const len = formData.body.length;
                                const remaining = limit?.max ? limit.max - len : null;
                                const segments = limit?.segment ? Math.ceil(len / limit.segment) : null;
                                const overWarn = limit?.warn && len >= limit.warn;
                                const overMax = limit?.max && len > limit.max;
                                const counterColor = overMax ? 'text-red-600' : overWarn ? 'text-amber-600' : 'text-[#4B5878]';
                                return (
                                    <>
                                        <div className="flex items-center justify-between mb-1">
                                            <Label className="text-sm font-medium text-[#1F2D4B]">Contenido del mensaje</Label>
                                            <div className={`text-xs font-mono-brand font-medium ${counterColor} flex items-center gap-2`}>
                                                {overMax && <Warning size={12} weight="bold" />}
                                                <span>
                                                    {limit?.max
                                                        ? `${len} / ${limit.max}`
                                                        : `${len} car`}
                                                    {segments !== null && segments > 0 && ` · ${segments} SMS`}
                                                </span>
                                            </div>
                                        </div>
                                        <textarea
                                            value={formData.body}
                                            onChange={(e) => setFormData({ ...formData, body: e.target.value })}
                                            placeholder="Escribe tu mensaje aquí..."
                                            rows={6}
                                            data-testid="template-body-input"
                                            className={`w-full rounded-xl border bg-white text-[#1F2D4B] px-4 py-3 text-sm resize-none focus:outline-none focus:ring-1 placeholder:text-[#9CA3AF] ${
                                                overMax
                                                    ? 'border-red-400 focus:ring-red-400'
                                                    : overWarn
                                                        ? 'border-amber-400 focus:ring-amber-400'
                                                        : 'border-[#ECE3D2] focus:ring-[#FB7185]'
                                            }`}
                                        />
                                        {/* Helper text */}
                                        {limit?.max && (
                                            <div className="mt-1.5 flex items-center justify-between text-[11px]">
                                                <span className="text-[#8B8474]">
                                                    {limit.label === 'SMS'
                                                        ? `Máx ${limit.max} caracteres por SMS. Mensajes más largos se dividen.`
                                                        : `Máximo ${limit.max} caracteres en ${limit.label}`}
                                                </span>
                                                {remaining !== null && (
                                                    <span className={`font-medium ${counterColor}`}>
                                                        {remaining >= 0
                                                            ? `Te quedan ${remaining}`
                                                            : `${Math.abs(remaining)} de más`}
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                        {formData.channel === 'email' && (
                                            <p className="mt-1.5 text-[11px] text-[#8B8474]">
                                                Sin límite de caracteres en Email
                                            </p>
                                        )}
                                    </>
                                );
                            })()}
                        </div>

                        {/* Variables */}
                        {/* Vista previa con datos de muestra + validación */}
                        {formData.body && (() => {
                            const { preview, invalidVars } = analyzeTemplate(formData.body);
                            return (
                                <div className="space-y-2">
                                    {invalidVars.length > 0 && (
                                        <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2.5 flex items-start gap-2" data-testid="invalid-vars-warning">
                                            <span className="text-red-500 text-base leading-none">⚠️</span>
                                            <div className="flex-1">
                                                <p className="text-xs font-semibold text-red-700 mb-1">
                                                    Variables que no existen ({invalidVars.length})
                                                </p>
                                                <p className="text-[11px] text-red-600 mb-1">
                                                    Estas variables se mandarán literales al cliente. Bórralas o reemplázalas:
                                                </p>
                                                <div className="flex flex-wrap gap-1.5">
                                                    {invalidVars.map(v => (
                                                        <code key={v} className="text-[11px] bg-red-100 text-red-700 px-1.5 py-0.5 rounded font-mono">
                                                            {`{${v}}`}
                                                        </code>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    <details className="bg-[#F0F7F4] border border-[#86EFAC]/40 rounded-lg" data-testid="preview-toggle">
                                        <summary className="px-3 py-2 cursor-pointer text-xs font-semibold text-[#1F2D4B] flex items-center gap-1.5">
                                            <span>👁️</span> Vista previa (con datos de ejemplo)
                                        </summary>
                                        <div className="px-3 pb-3 pt-1 border-t border-[#86EFAC]/30">
                                            <pre className="text-[12px] text-[#1F2D4B] whitespace-pre-wrap break-words font-sans leading-snug" data-testid="preview-content">
{preview}
                                            </pre>
                                        </div>
                                    </details>
                                </div>
                            );
                        })()}

                        <div>
                            <Label className="text-xs text-[#4B5878] mb-2 block">Variables disponibles (click para insertar):</Label>
                            <div className="flex flex-wrap gap-2">
                                {variables.map((v) => (
                                    <button
                                        key={v}
                                        onClick={() => {
                                            setFormData({...formData, body: formData.body + v});
                                            toast.success(`Variable ${v} agregada`);
                                        }}
                                        className="px-2 py-1 rounded-lg bg-[#F8F4EC] border border-[#ECE3D2] text-xs font-mono text-[#4B5878] hover:bg-[#E5E7EB] transition-colors"
                                    >
                                        {v}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* AI Options */}
                        <div className="border-t border-[#ECE3D2] pt-4">
                            <p className="text-sm font-medium text-[#1F2D4B] mb-3 flex items-center gap-2">
                                <MagicWand size={16} className="text-[#FB7185]" />
                                Asistente IA
                            </p>
                            
                            {!aiMode ? (
                                <div className="grid grid-cols-2 gap-3">
                                    <button
                                        onClick={() => setAiMode('assist')}
                                        className="p-3 rounded-xl border border-[#ECE3D2] hover:border-[#1F2D4B]/50 transition-colors text-left"
                                    >
                                        <PencilSimple size={18} className="text-[#FB7185] mb-1" />
                                        <p className="text-sm font-medium text-[#1F2D4B]">Mejorar texto</p>
                                        <p className="text-xs text-[#4B5878]">La IA mejora tu borrador actual</p>
                                    </button>
                                    <button
                                        onClick={() => setAiMode('full')}
                                        className="p-3 rounded-xl border border-[#ECE3D2] hover:border-[#1F2D4B]/50 transition-colors text-left"
                                    >
                                        <Robot size={18} className="text-[#FB7185] mb-1" />
                                        <p className="text-sm font-medium text-[#1F2D4B]">Generar completo</p>
                                        <p className="text-xs text-[#4B5878]">La IA crea el mensaje desde cero</p>
                                    </button>
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {aiMode === 'full' && (
                                        <div>
                                            <Label className="text-xs text-[#4B5878]">Instrucción para la IA:</Label>
                                            <Input
                                                value={aiInstruction}
                                                onChange={(e) => setAiInstruction(e.target.value)}
                                                placeholder="Ej: Mensaje cálido y profesional que invite a reservar pronto"
                                                className="mt-1 h-11 rounded-xl border-[#ECE3D2] bg-white text-[#1F2D4B]"
                                                data-testid="ai-instruction-input"
                                            />
                                        </div>
                                    )}
                                    {aiMode === 'assist' && (
                                        <p className="text-xs text-[#4B5878]">
                                            La IA mejorará el texto que ya escribiste arriba.
                                        </p>
                                    )}
                                    <div className="flex gap-2">
                                        <Button
                                            onClick={handleAIGenerate}
                                            disabled={generating || (aiMode === 'full' && !aiInstruction)}
                                            data-testid="generate-ai-btn"
                                            className="bg-[#1F2D4B] hover:bg-[#2E3F60] text-white rounded-xl"
                                        >
                                            <Lightning size={16} className="mr-2" />
                                            {generating ? 'Generando...' : 'Generar'}
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            onClick={() => setAiMode(null)}
                                            className="text-[#4B5878] rounded-xl"
                                        >
                                            Cancelar
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Actions */}
                        <div className="flex gap-3 pt-2">
                            <Button
                                variant="outline"
                                onClick={() => { setShowEditor(false); resetForm(); }}
                                className="flex-1 h-11 rounded-xl border-[#ECE3D2]"
                            >
                                Cancelar
                            </Button>
                            <Button
                                onClick={handleSave}
                                data-testid="save-template-btn"
                                className="flex-1 h-11 bg-[#1F2D4B] hover:bg-[#2E3F60] text-white rounded-xl"
                            >
                                {editingTemplate ? 'Guardar Cambios' : 'Crear Plantilla'}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Preview Modal */}
            <Dialog open={!!previewTemplate} onOpenChange={() => setPreviewTemplate(null)}>
                <DialogContent className="sm:max-w-lg rounded-2xl">
                    <DialogHeader>
                        <DialogTitle className="font-editorial text-xl text-[#1F2D4B] flex items-center gap-2">
                            <Eye size={20} className="text-[#FB7185]" />
                            Vista Previa
                        </DialogTitle>
                    </DialogHeader>
                    
                    {previewTemplate && (
                        <div className="space-y-4 pt-2">
                            {/* Channel indicator */}
                            <div className="flex items-center gap-2">
                                {channelIcon(previewTemplate.channel)}
                                <span className="text-sm font-medium text-[#1F2D4B]">{previewTemplate.name}</span>
                                <Badge className={`border-0 text-xs ml-auto ${
                                    previewTemplate.type === 'welcome' ? 'bg-purple-50 text-purple-700' :
                                    previewTemplate.type === 'sale_confirmation' ? 'bg-[#D1FAE5] text-[#059669]' :
                                    'bg-amber-50 text-amber-700'
                                }`}>
                                    {typeLabel(previewTemplate.type)}
                                </Badge>
                            </div>

                            {/* Preview mockup */}
                            <div className={`rounded-2xl overflow-hidden border ${
                                previewTemplate.channel === 'whatsapp' ? 'border-[#25D366]/30 bg-[#ECE5DD]' :
                                previewTemplate.channel === 'sms' ? 'border-[#ECE3D2] bg-[#F8F4EC]' :
                                'border-[#ECE3D2] bg-white'
                            }`}>
                                {/* Channel header */}
                                <div className={`px-4 py-2 text-xs font-medium ${
                                    previewTemplate.channel === 'whatsapp' ? 'bg-[#075E54] text-white' :
                                    previewTemplate.channel === 'sms' ? 'bg-[#1F2D4B] text-white' :
                                    'bg-[#F9F8F5] text-[#4B5878] border-b border-[#ECE3D2]'
                                }`}>
                                    {previewTemplate.channel === 'whatsapp' && 'WhatsApp - +5213333202200'}
                                    {previewTemplate.channel === 'sms' && 'SMS - +18507905327'}
                                    {previewTemplate.channel === 'email' && (
                                        <div>
                                            <p className="text-[#1F2D4B] font-medium">De: certificados@exploremos.mx</p>
                                            <p className="text-[#4B5878]">Asunto: {fillPreview(previewTemplate.subject || 'Sin asunto')}</p>
                                        </div>
                                    )}
                                </div>
                                
                                {/* Message bubble */}
                                <div className="p-4">
                                    <div className={`p-3 rounded-xl max-w-[85%] ${
                                        previewTemplate.channel === 'whatsapp' ? 'bg-[#DCF8C6] text-[#303030]' :
                                        previewTemplate.channel === 'sms' ? 'bg-[#0B93F6] text-white' :
                                        'bg-white text-[#1F2D4B]'
                                    }`}>
                                        <p className="text-sm whitespace-pre-line leading-relaxed">
                                            {fillPreview(previewTemplate.body)}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Sample data reference */}
                            <div className="p-3 rounded-xl bg-[#F9F8F5] border border-[#ECE3D2]">
                                <p className="text-xs font-medium text-[#4B5878] mb-2">Datos de ejemplo usados:</p>
                                <div className="grid grid-cols-2 gap-1 text-xs text-[#4B5878]">
                                    {Object.entries(sampleData).map(([key, val]) => (
                                        <p key={key}><span className="font-mono text-[#FB7185]">{`{${key}}`}</span> = {val.length > 25 ? val.slice(0, 25) + '...' : val}</p>
                                    ))}
                                </div>
                            </div>

                            <Button
                                onClick={() => setPreviewTemplate(null)}
                                className="w-full h-11 bg-[#1F2D4B] hover:bg-[#2D3A5A] text-white rounded-xl"
                            >
                                Cerrar
                            </Button>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default MessageTemplatesPage;
